import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import slugify from "slugify";

import DashboardLayout from "../DashboardLayout";
import ProfileStyle from "../../../assets/stylesheets/dashboard/profile.module.css";

import { getMyBookmarks, deleteBookmark } from "../../../api/user-service/bookmarkService";
import { getFarmById } from "../../../api/farm-service/farmService";

function normalizeListResponse(res) {
  const data = res?.data ?? res;
  if (Array.isArray(data)) return { items: data, total: data.length };

  const items = data?.items ?? data?.results ?? data?.rows ?? [];
  const total = data?.total ?? data?.count ?? data?.meta?.total ?? items.length;
  return { items, total };
}

function getBookmarkedAt(item) {
  return item?.bookmarkedAt || item?.createdAt || item?.updatedAt || item?.created_at || null;
}

function formatSavedAt(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" });
}

export default function BookmarksPage() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [isMutating, setIsMutating] = useState(false);

  useEffect(() => {
    let alive = true;

    async function load() {
      setIsLoading(true);
      setError("");
      setSelected(new Set());

      try {
        const query = { order: "DESC", sort: "createdAt", limit: 50 };
        const res = await getMyBookmarks(query);
        const { items: listItems, total: listTotal } = normalizeListResponse(res);

        if (!alive) return;

        const sorted = [...listItems].sort((a, b) => {
          const ad = new Date(getBookmarkedAt(a) || 0).getTime();
          const bd = new Date(getBookmarkedAt(b) || 0).getTime();
          return bd - ad;
        });

        setItems(sorted);
        setTotal(listTotal ?? sorted.length);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || "Failed to load");
      } finally {
        if (!alive) return;
        setIsLoading(false);
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, []);

  const rows = useMemo(() => {
    return items.map((it) => {
      const targetType = it?.targetType ?? it?.target_type;
      const targetId = it?.targetId ?? it?.target_id ?? it?.target?.id;

      return {
        key: `bookmarks-${it?.id ?? ""}-${targetType ?? ""}-${targetId ?? ""}`,
        id: it?.id,
        title: it?.title ?? it?.name ?? it?.targetTitle ?? "(Untitled)",
        description: it?.description ?? it?.note ?? "",
        targetType,
        targetId,
        savedAt: getBookmarkedAt(it),
      };
    });
  }, [items]);

  const selectedCount = selected.size;

  const toggleSelected = (bookmarkId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(bookmarkId)) next.delete(bookmarkId);
      else next.add(bookmarkId);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(rows.map((r) => r.id).filter(Boolean)));
  };

  const clearSelection = () => setSelected(new Set());

  const handleRemoveSelected = async () => {
    if (selectedCount === 0) return;

    setIsMutating(true);
    try {
      const ids = Array.from(selected);
      await Promise.all(ids.map((id) => deleteBookmark(id)));

      // Optimistic UI update
      setItems((prev) => prev.filter((it) => !selected.has(it?.id)));
      clearSelection();
    } finally {
      setIsMutating(false);
    }
  };

  const handleRowClick = async (row) => {
    // For now, only FARM is supported for navigation.
    const type = String(row?.targetType || "").toLowerCase();
    if (type !== "farm") {
      setError("This bookmark type is not supported yet.");
      return;
    }

    if (!row?.targetId) return;

    try {
      const res = await getFarmById(row.targetId);
      const farm = res?.data ?? res;
      const slug = slugify(farm?.name || "farm", { lower: true, strict: true });
      navigate(`/farm/${row.targetId}/${slug}`);
    } catch {
      setError("Failed to open farm.");
    }
  };

  return (
    <DashboardLayout>
      <div className={ProfileStyle["section"]}>
        <div className={ProfileStyle["headerRow"]}>
          <div>
            <h2 className={ProfileStyle["title"]}>My Bookmarks</h2>
          </div>
          <div className={ProfileStyle["countBadge"]} title="Total">
            {total || rows.length}
          </div>
        </div>

        <div className={ProfileStyle["card"]}>
          <div className={ProfileStyle["toolbar"]}>
            <div className={ProfileStyle["toolbarLeft"]}>
              <button
                type="button"
                className={ProfileStyle["actionBtn"]}
                onClick={selectAll}
                disabled={rows.length === 0}
              >
                Select all
              </button>

              <button
                type="button"
                className={ProfileStyle["actionBtn"]}
                onClick={clearSelection}
                disabled={selectedCount === 0}
              >
                Clear
              </button>
              <span className={ProfileStyle["smallText"]}>
                Selected: <b>{selectedCount}</b>
              </span>
            </div>

            <button
              type="button"
              className={ProfileStyle["actionBtn"]}
              onClick={handleRemoveSelected}
              disabled={selectedCount === 0 || isMutating}
              title="Remove bookmarks"
            >
              {isMutating ? "Removing..." : "Remove bookmarks"}
            </button>
          </div>

          {isLoading ? (
            <div className={ProfileStyle["empty"]}>Loading...</div>
          ) : error ? (
            <div className={ProfileStyle["empty"]}>{error}</div>
          ) : rows.length === 0 ? (
            <div className={ProfileStyle["empty"]}>There are no bookmarks.</div>
          ) : (
            <div className={ProfileStyle["list"]}>
              {rows.map((row) => {
                const checked = selected.has(row.id);

                return (
                  <div
                    key={row.key}
                    className={ProfileStyle["row"]}
                    onClick={() => handleRowClick(row)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRowClick(row);
                    }}
                  >
                    <input
                      type="checkbox"
                      className={ProfileStyle["checkbox"]}
                      checked={checked}
                      onChange={() => toggleSelected(row.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${row.title}`}
                    />

                    <div aria-hidden="true" />

                    <div>
                      <p className={ProfileStyle["name"]}>{row.title}</p>
                      <p className={ProfileStyle["meta"]}>
                        {row.description ? row.description : ""}
                      </p>
                      <p className={ProfileStyle["meta"]}>
                        <b>{String(row.targetType || "").toUpperCase()}</b>
                        {row.targetId ? ` · #${row.targetId}` : ""}
                        {row.savedAt ? ` · ${formatSavedAt(row.savedAt)}` : ""}
                      </p>
                    </div>

                    <div className={ProfileStyle["right"]}>
                      <div className={ProfileStyle["date"]}>{formatSavedAt(row.savedAt)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
